<template>
  <div class="home-bottom-view">
    <!-- Category Tabs -->
    <div class="header-sticky">
      <div class="category-tab-scroll">
        <div
          v-for="(item, index) in tabs"
          :key="index"
          class="category-tab-item"
          :class="{ active: currentIndex === index }"
          @click="onTabClick(index)"
        >
          {{ item.tabName }}
        </div>
      </div>
    </div>
    
    <!-- Goods List -->
    <van-list
      v-model="loading"
      :finished="finished"
      finished-text="~ 我是有底线的 ~"
      @load="loadMoreData"
    >
      <div 
        :style="{ 
          opacity: loading && page.pageNo === 1 ? 0.5 : 1, 
          transition: 'opacity 0.2s',
          pointerEvents: loading && page.pageNo === 1 ? 'none' : 'auto'
        }"
      >
        <div v-if="isStore" class="goods-list-b">
          <goods-cell-b
            v-for="item in currentGoodsList"
            :key="item.id"
            :item="item"
            @click="onGoodsClick"
            @clickLeft="onLeftBtnClick"
            @clickShare="onShareClick"
          />
        </div>
        <div v-else class="goods-grid">
          <div
            v-for="(item, index) in currentGoodsList"
            :key="item.id"
            class="goods-cell-grid-wrapper"
            :class="{ item_left: index % 2 !== 0 }"
          >
            <product-ver-com :product="item" @showPoster="onShareClick" />
          </div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-if="currentGoodsList.length === 0 && !loading" class="goods-list-empty-fallback">
        <img src="@/assets/common/meiyoushangpin.png" class="empty-icon-img" />
        <div class="fallback-empty-text">暂无商品</div>
      </div>
    </van-list>
  </div>
</template>

<script>
import GoodsCellB from "./GoodsCellB.vue";
import ProductVerCom from "@/components/ProductVerCom";
import { homeGetAllGoods } from "@/api/home";

export default {
  name: "HomeBottomView",
  components: {
    GoodsCellB,
    ProductVerCom
  },
  props: {
    bottomListModel: {
      type: Object,
      default: () => ({})
    },
    isStore: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentIndex: 0,
      currentGoodsList: [],
      page: {
        pageNo: 1,
        pageSize: 10
      },
      loading: false,
      finished: false
    };
  },
  computed: {
    tabs() {
      return (this.bottomListModel && this.bottomListModel.retailHomepageDetails) || [];
    }
  },
  watch: {
    bottomListModel: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.retailHomepageDetails && newVal.retailHomepageDetails.length > 0) {
          this.currentIndex = 0;
          this.reloadData();
        }
      }
    }
  },
  methods: {
    reloadData() {
      this.page.pageNo = 1;
      this.finished = false;
      this.getGoodsList(true);
    },
    loadMoreData() {
      if (this.finished) return;
      this.page.pageNo++;
      this.getGoodsList(false);
    },
    async getGoodsList(isRefresh = false) {
      if (this.tabs.length === 0) {
        this.loading = false;
        this.finished = true;
        return;
      }
      this.loading = true;
      const categoryId = this.tabs[this.currentIndex]?.retailFrontCategoryId;
      const params = {
        categoryId: categoryId,
        pageNo: this.page.pageNo,
        pageSize: this.page.pageSize,
        slug: this.slug // Global mixin slug
      };
      
      try {
        const res = await homeGetAllGoods(params);
        let listData = [];
        let hasNextPage = false;
        if (res && res.code === 1 && res.data) {
          listData = res.data.rows || [];
          hasNextPage = res.data.hasNextPage !== false;
        }
        if (isRefresh) {
          this.currentGoodsList = listData;
        } else {
          this.currentGoodsList = [...this.currentGoodsList, ...listData];
        }
        if (!hasNextPage || listData.length < this.page.pageSize) {
          this.finished = true;
        } else {
          this.finished = false;
        }
      } catch (err) {
        console.error("加载底部商品列表失败", err);
        this.finished = true;
      } finally {
        this.loading = false;
      }
    },
    onTabClick(index) {
      if (index === this.currentIndex) return;
      this.currentIndex = index;
      this.$emit("tabChange", {
        index: this.currentIndex,
        categoryId: this.tabs[this.currentIndex]?.retailFrontCategoryId
      });
      
      // 重置 Y 轴滑动高度：如果当前页面滚动已经超过分类栏吸顶的位置，则把滚动重置定位到吸顶位置
      const element = this.$el;
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const absoluteTop = rect.top + scrollTop;
        // 头部 fixed 高度为 96px
        const targetScrollTop = absoluteTop - 96;
        if (scrollTop > targetScrollTop) {
          window.scrollTo({
            top: targetScrollTop,
            behavior: "auto"
          });
        }
      }

      this.reloadData();
    },
    onGoodsClick(item) {
      this.$router.push({
        path: "/product/detail",
        query: { id: item.id }
      });
    },
    onLeftBtnClick(item) {
      // 触发上架，可以在此处调用 API 或传给父组件
      this.$emit("clickLeft", item);
    },
    onShareClick(item) {
      // 触发分享
      this.$emit("showPoster", item);
    }
  }
};
</script>

<style lang="less" scoped>
.home-bottom-view {
  width: 100%;
  background-color: #f7f8fa;
  padding-bottom: 60px;
}
.header-sticky {
  position: sticky;
  top: 96px;
  z-index: 1;
  background-color: #fff;
  border-bottom: 1px solid #ebedf0;
}

.category-tab-scroll {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  padding: 7px 0;
  background: #fff;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
}
.category-tab-item {
  display: inline-block;
  padding: 0 12px;
  height: 30px;
  line-height: 30px;
  font-size: 12px;
  color: #565656;
  background-color: #F1F1F1;
  border-radius: 15px;
  margin-right: 6px;
  vertical-align: top;
  &:first-child {
    margin-left: 12px;
  }
  &:last-child {
    margin-right: 12px;
  }
  &.active {
    background-color: rgba(223, 159, 91, 0.29);
    color: #BF9264;
    font-weight: normal;
  }
}
.goods-list-b {
  padding: 8px;
  background-color: transparent;
}
.goods-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 8px;
  box-sizing: border-box;
}
.goods-cell-grid-wrapper {
  width: 48.5%;
  margin-bottom: 8px;
}
.item_left {
  // 可以根据实际布局微调微小的间距
}
.goods-list-empty-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: #969799;
}
.empty-icon-img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 8px;
}
.fallback-empty-text {
  font-size: 13px;
  color: #999;
}
</style>
